import { useState } from 'react';
import { User, Lock, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import logo from '../logo.png';

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(-125deg,#9b2c2c,#d64545)',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
    padding: '36px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  logoWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
  },
  logoBadge: {
    width: 72,
    height: 72,
    borderRadius: 18,
    background: 'linear-gradient(-125deg,#9b2c2c,#d64545)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 18px rgba(155,44,44,0.35)',
  },
  logoImg: { width: 44, height: 'auto', filter: 'brightness(0) invert(1)' },
  title: { fontSize: 20, fontWeight: 800, color: '#1a1a2e', letterSpacing: '-0.02em' },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: -6 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#6b7280',
  },
  inputWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    border: '1.5px solid #e2e5ea',
    borderRadius: 10,
    padding: '10px 12px',
    background: '#f9fafb',
    transition: 'border-color 0.15s, background 0.15s',
  },
  inputWrapFocus: { borderColor: '#9b2c2c', background: '#fff' },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    color: '#1a1a2e',
  },
  eyeBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex',
    padding: 0,
  },
  button: {
    marginTop: 6,
    height: 44,
    border: 'none',
    borderRadius: 10,
    background: 'linear-gradient(-125deg,#9b2c2c,#d64545)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    boxShadow: '0 4px 14px rgba(155,44,44,0.3)',
    transition: 'opacity 0.15s, transform 0.05s',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fff1f1',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    fontSize: 12.5,
    fontWeight: 500,
    borderRadius: 8,
    padding: '9px 11px',
  },
  footer: { textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 2 },
};

export default function Login() {
  const { login } = useAuth();
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [focus, setFocus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await login(id.trim(), password);
    } catch (err) {
      setError(err.message || 'Login gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <form style={s.card} onSubmit={submit}>
        <div style={s.logoWrap}>
            <img src={logo} alt="Logo" />
          <div style={{ textAlign: 'center' }}>
            {/* <div style={s.title}>Sales Dashboard</div> */}
          </div>
        </div>

        {error && (
          <div style={s.error}>
            <AlertCircle size={15} />
            <span>{error}</span>
          </div>
        )}

        <div style={s.field}>
          <label style={s.label}>User ID</label>
          <div style={{ ...s.inputWrap, ...(focus === 'id' ? s.inputWrapFocus : {}) }}>
            <User size={16} color="#9ca3af" />
            <input
              style={s.input}
              type="text"
              autoFocus
              autoComplete="username"
              placeholder="Enter user id"
              value={id}
              onChange={(e) => setId(e.target.value)}
              onFocus={() => setFocus('id')}
              onBlur={() => setFocus('')}
            />
          </div>
        </div>

        <div style={s.field}>
          <label style={s.label}>Password</label>
          <div style={{ ...s.inputWrap, ...(focus === 'pw' ? s.inputWrapFocus : {}) }}>
            <Lock size={16} color="#9ca3af" />
            <input
              style={s.input}
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocus('pw')}
              onBlur={() => setFocus('')}
            />
            <button type="button" style={s.eyeBtn} onClick={() => setShowPw((v) => !v)} tabIndex={-1}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <button
          style={{ ...s.button, opacity: loading ? 0.7 : 1, cursor: loading ? 'default' : 'pointer' }}
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spin" style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%' }} />
              Memproses...
            </>
          ) : (
            <>
              <LogIn size={16} />
              Login
            </>
          )}
        </button>

        <div style={s.footer}>© {new Date().getFullYear()} Sales Dashboard</div>
      </form>
    </div>
  );
}
